import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import CurrencySelector from "../CurrencySelector";
import { useFormContext } from "../../context/FormContext";
import { useCurrency } from "../../context/CurrencyContext";
import logo from "../../images/rg_logo-2.webp";
const NAV_LINKS = [
  { label: "Home", path: "/" },
  { label: "Manage Booking", path: "/manage-booking" },
  { label: "About Us", path: "/about-us" },
  { label: "Privacy Policy", path: "/policy" },
  { label: "Terms and Conditions", path: "/terms-and-conditions" },
  { label: "Contact us", path: "/support" },
];

export default function Navbar() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { selectedCurrency, setSelectedCurrency } = useCurrency();
  const [showCurrencyConfirm, setShowCurrencyConfirm] = useState(false);
  const [pendingCurrency, setPendingCurrency] = useState(null);
  const isCheckoutPage = location.pathname === "/checkout";

  const handleCurrencyChange = (currency) => {
    if (currency?.code === selectedCurrency?.code) return;
    if (isCheckoutPage) {
      setPendingCurrency(currency);
      setShowCurrencyConfirm(true);
    } else {
      setSelectedCurrency(currency);
    }
  };

  const handleCurrencyConfirm = () => {
    setSelectedCurrency(pendingCurrency);
    window.dispatchEvent(new Event("currencyChange"));
    setShowCurrencyConfirm(false);
    setPendingCurrency(null);
  };

  const handleCurrencyCancel = () => {
    setShowCurrencyConfirm(false);
    setPendingCurrency(null);
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const isHomePage = location.pathname === "/";
  const { canUseTestMode, isTestMode, setIsTestMode } = useFormContext();

  const navbarBgClasses = isHomePage
    ? scrolled
      ? "bg-black/60 backdrop-blur-md shadow-lg"
      : "bg-black/20 backdrop-blur-sm"
    : scrolled
      ? "bg-neutral-950/80 backdrop-blur-md shadow-lg"
      : "bg-neutral-900/90 backdrop-blur-md";

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-white/10 ${navbarBgClasses}`}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          {/* Logo Container */}
          <Link to="/" className="flex items-center group">
            <img 
              src={logo} 
              alt="Rent Gurus" 
              className="h-11 w-auto object-contain transition-transform group-hover:scale-105"
            />
          </Link>

          {/* Desktop Nav Links */}
          <div className="hidden md:flex items-center gap-8">
            {NAV_LINKS.slice(0, 3).map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`font-body text-sm font-medium transition-colors relative py-1 ${
                    isActive ? "text-white" : "text-white/80 hover:text-white"
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
                  )}
                </Link>
              );
            })}
          </div>

          {/* Desktop Right Side */}
          <div className="hidden md:flex items-center gap-6 text-white">
            {canUseTestMode && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-body font-semibold uppercase text-white/70">
                  TEST MODE
                </span>
                <button
                  type="button"
                  onClick={() => setIsTestMode((p) => !p)}
                  className={`relative w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none ${
                    isTestMode ? "bg-primary" : "bg-white/20"
                  }`}
                >
                  <span
                    className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                      isTestMode ? "translate-x-4" : "translate-x-0"
                    }`}
                  />
                </button>
                <span
                  className={`text-xs font-body font-bold transition-colors ${
                    isTestMode ? "text-primary" : "text-white/40"
                  }`}
                >
                  {isTestMode ? "ON" : "OFF"}
                </span>
              </div>
            )}
            <CurrencySelector
              selectedCurrency={selectedCurrency}
              onCurrencyChange={handleCurrencyChange}
            />
            <Link
              to="/support"
              className="flex items-center gap-2 bg-primary hover:bg-primary-hover
                text-white text-sm font-medium px-4 py-2 rounded-xl transition-colors shadow-sm"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.2"
              >
                <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
              </svg>
              Support
            </Link>
          </div>

          {/* Mobile Right — currency + hamburger */}
          <div className="md:hidden flex items-center gap-2 text-white">
            <CurrencySelector
              selectedCurrency={selectedCurrency}
              onCurrencyChange={handleCurrencyChange}
            />
            <button
              className="text-white p-2"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M3 12h18M3 6h18M3 18h18" />
              </svg>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Drawer Overlay Container */}
      <div
        className={`fixed inset-0 z-50 md:hidden transition-all duration-300 ${
          menuOpen
            ? "visible pointer-events-auto"
            : "invisible pointer-events-none"
        }`}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${
            menuOpen ? "opacity-100" : "opacity-0"
          }`}
          onClick={() => setMenuOpen(false)}
        />

        {/* Drawer Panel */}
        <div
          className={`absolute top-0 right-0 bottom-0 w-4/5 max-w-xs bg-white 
          border-l border-neutral-100 flex flex-col shadow-xl text-neutral-800
          transition-transform duration-300 ease-out ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          {/* Drawer Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-neutral-100">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="flex items-center"
            >
              {/* In the side drawer (white background), consider switching to a version with dark text if this white/green layout blends in too much */}
              <img 
                src={logo} 
                alt="Rent Gurus" 
                className="h-6 w-auto object-contain brightness-0 invert-0" 
              />
            </Link>
            <button
              onClick={() => setMenuOpen(false)}
              className="text-neutral-500 hover:text-neutral-800 p-1"
              aria-label="Close menu"
            >
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Drawer Nav Links */}
          <div className="flex flex-col px-5 py-4 gap-1 flex-1 overflow-y-auto">
            {NAV_LINKS.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMenuOpen(false)}
                style={{
                  transitionDelay: menuOpen ? `${index * 40}ms` : "0ms",
                }}
                className={`text-neutral-700 font-body text-base font-medium py-3 border-b border-neutral-50 hover:text-primary transition-all duration-300 ${
                  menuOpen
                    ? "translate-x-0 opacity-100"
                    : "translate-x-4 opacity-0"
                }`}
              >
                {link.label}
              </Link>
            ))}

            {/* Mobile Test Mode Toggle */}
            {canUseTestMode && (
              <div className="flex items-center justify-between py-3 border-b border-neutral-50">
                <span className="text-xs font-body font-semibold uppercase text-neutral-500">
                  TEST MODE
                </span>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsTestMode((p) => !p)}
                    className={`relative w-9 h-5 rounded-full transition-colors duration-200 focus:outline-none ${
                      isTestMode ? "bg-primary" : "bg-neutral-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                        isTestMode ? "translate-x-4" : "translate-x-0"
                      }`}
                    />
                  </button>
                  <span
                    className={`text-xs font-body font-bold transition-colors ${
                      isTestMode ? "text-primary" : "text-neutral-400"
                    }`}
                  >
                    {isTestMode ? "ON" : "OFF"}
                  </span>
                </div>
              </div>
            )}

            {/* Drawer Footer */}
            <div
              className={`pt-6 transition-all duration-300 ${
                menuOpen
                  ? "translate-y-0 opacity-100"
                  : "translate-y-4 opacity-0"
              }`}
              style={{
                transitionDelay: menuOpen
                  ? `${NAV_LINKS.length * 40}ms`
                  : "0ms",
              }}
            >
              <Link
                to="/support"
                onClick={() => setMenuOpen(false)}
                className="flex items-center justify-center gap-2 bg-primary
                hover:bg-primary-hover text-white text-sm font-medium
                py-3 rounded-xl w-full transition-colors shadow-md"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
                  <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
                </svg>
                Support
              </Link>
            </div>
          </div>
        </div>
      </div>
      {/* Currency Change Confirmation Modal (Checkout only) */}
      {showCurrencyConfirm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-surface rounded-2xl shadow-card max-w-sm w-full p-6 border border-surface-border">
            <div className="flex flex-col items-center text-center">
              <div className="bg-primary-soft p-3 rounded-full mb-3">
                <svg
                  className="w-6 h-6 text-primary"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                  />
                </svg>
              </div>
              <h3 className="text-lg font-heading font-bold text-text mb-1">
                Change Currency?
              </h3>
              <p className="text-text font-body text-xs leading-relaxed px-2 mb-5">
                Changing currency to{" "}
                <span className="text-text font-semibold">
                  {pendingCurrency?.code}
                </span>{" "}
                will take you back to the search results with updated prices.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              <button
                onClick={handleCurrencyConfirm}
                className="w-full py-3.5 rounded-xl font-heading font-bold text-sm bg-primary text-white hover:bg-primary-hover transition-all"
              >
                Yes, Change Currency
              </button>
              <button
                onClick={handleCurrencyCancel}
                className="w-full py-2 text-text-muted font-heading font-bold text-xs hover:text-text transition-colors tracking-wide"
              >
                CANCEL
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}